import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Upload, Download, Trash2, Plus, AlertTriangle } from "lucide-react";
import api, { formatErr } from "@/lib/api";

/**
 * Site Operating Calendar — per-site working days + ad-hoc closures.
 *
 * Layout:
 *   • Weekly schedule: 7 checkboxes (Mon..Sun). Owners/managers only.
 *   • Closures list: filtered by year, add / remove / bulk-import from CSV.
 *
 * Closure `type` is either PH (Public Holiday) or CL (Closed) — the
 * downstream PDFs render exactly those two labels + a legend.
 */
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function SiteCalendarPanel({ site, canEdit }) {
  const [workingDays, setWorkingDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [closures, setClosures] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [adding, setAdding] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newType, setNewType] = useState("PH");
  const [newReason, setNewReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Data collection start date — per-site override with tenant fallback.
  const [startDate, setStartDate] = useState("");           // effective value shown
  const [startOverride, setStartOverride] = useState("");   // "" ⇒ inheriting
  const [tenantDefault, setTenantDefault] = useState("");
  const [startBusy, setStartBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [wd, cl, ds] = await Promise.all([
        api.get(`/sites/${site.id}/working-days`),
        api.get(`/sites/${site.id}/closures`, { params: { year } }),
        api.get(`/sites/${site.id}/data-start-date`),
      ]);
      setWorkingDays(wd.data.working_days || [0, 1, 2, 3, 4, 5, 6]);
      setClosures(cl.data.closures || []);
      setStartDate(ds.data.data_start_date || "");
      setStartOverride(ds.data.override || "");
      setTenantDefault(ds.data.tenant_default || "");
    } catch { /* ignored */ }
  };
  useEffect(() => { if (site?.id) load(); }, [site?.id, year]); // eslint-disable-line

  const saveStartDate = async (val) => {
    if (!canEdit) return;
    setStartBusy(true);
    try {
      const { data } = await api.patch(`/sites/${site.id}/data-start-date`, {
        data_start_date: val || "",
      });
      setStartDate(data.data_start_date || "");
      setStartOverride(data.override || "");
      setTenantDefault(data.tenant_default || "");
      toast.success(val ? "Start date updated" : "Reverted to tenant default");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to save");
    } finally { setStartBusy(false); }
  };

  const toggleDay = async (d) => {
    if (!canEdit) return;
    const next = workingDays.includes(d)
      ? workingDays.filter((x) => x !== d)
      : [...workingDays, d].sort((a, b) => a - b);
    setWorkingDays(next);
    try {
      await api.patch(`/sites/${site.id}/working-days`, { working_days: next });
      toast.success("Working days updated");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to save");
      load();
    }
  };

  const addClosure = async () => {
    if (!newDate) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      await api.post(`/sites/${site.id}/closures`, {
        date: newDate, type: newType, reason: newReason.trim(),
      });
      setAdding(false); setNewDate(""); setNewType("PH"); setNewReason("");
      await load();
      toast.success("Closure added");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const removeClosure = async (cid) => {
    if (!window.confirm("Remove this closure?")) return;
    try {
      await api.delete(`/sites/${site.id}/closures/${cid}`);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed");
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/sites/${site.id}/closures/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "site_closures_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    const form = new FormData(); form.append("file", file);
    try {
      const { data } = await api.post(`/sites/${site.id}/closures/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      if (data.errors?.length) {
        toast.warning(`Imported ${data.added}. ${data.errors.length} row(s) had errors — see console.`);
        console.warn("Import errors:", data.errors);
      } else {
        toast.success(`Imported ${data.added} closure(s).`);
      }
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Import failed");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const years = [year - 1, year, year + 1, year + 2];

  return (
    <div className="vb-card p-4 mt-4" data-testid={`site-calendar-${site.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="vb-heading text-base m-0" style={{ color: "var(--vb-text-2)" }}>
          <CalendarDays size={16} className="inline-block mr-1.5 -mt-0.5" />
          Operating Calendar
        </h3>
        <div className="text-[11px] opacity-60">
          Only <strong>operating days</strong> are counted in daily-tracking and CCP completeness.
          Non-working weekdays + closures (PH/CL) are excluded silently.
        </div>
      </div>

      {/* Data collection start date */}
      <div className="mb-4" data-testid="site-start-date">
        <div className="text-[11px] uppercase tracking-wider opacity-70 mb-1">
          Data collection start date
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            className="vb-input"
            style={{ height: 30, fontSize: 12, maxWidth: 180 }}
            value={startDate || ""}
            disabled={!canEdit || startBusy}
            onChange={(e) => setStartDate(e.target.value)}
            onBlur={(e) => {
              const v = e.target.value || "";
              // Only fire the patch if the value actually differs from
              // whatever the server currently sees as the effective date.
              const currentEffective = startOverride || tenantDefault;
              if (v !== currentEffective) saveStartDate(v);
            }}
            data-testid="site-start-date-input"
          />
          {canEdit && startOverride && (
            <button
              type="button"
              className="vb-btn ghost text-xs"
              onClick={() => saveStartDate("")}
              disabled={startBusy}
              data-testid="site-start-date-reset"
              title="Revert to tenant default"
            >
              Reset to tenant default
            </button>
          )}
        </div>
        <p className="text-[11px] opacity-60 mt-1">
          {startOverride
            ? "This site is using its own start date."
            : tenantDefault
              ? <>Inheriting the tenant default (<strong>{tenantDefault}</strong>) captured during onboarding. Set a value above to override for this site only.</>
              : "No tenant default set — pick a date to start tracking from."}
          {" "}Future dates are allowed if you're setting up ahead of go-live.
        </p>
      </div>

      {/* Weekly schedule */}
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider opacity-70 mb-1">Weekly schedule</div>
        <div className="flex gap-1 flex-wrap" data-testid="weekly-schedule">
          {DAY_LABELS.map((lbl, i) => {
            const on = workingDays.includes(i);
            return (
              <button key={lbl} type="button"
                onClick={() => toggleDay(i)}
                disabled={!canEdit}
                data-testid={`day-toggle-${i}`}
                style={{
                  padding: "6px 12px", borderRadius: 8, fontWeight: 600, fontSize: 12,
                  background: on ? "var(--vb-accent)" : "white",
                  color: on ? "white" : "var(--vb-text-2)",
                  border: `1px solid ${on ? "transparent" : "var(--vb-border)"}`,
                  cursor: canEdit ? "pointer" : "not-allowed",
                  opacity: canEdit ? 1 : 0.7,
                }}>{lbl}</button>
            );
          })}
        </div>
      </div>

      {/* Closures list */}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <div className="text-[11px] uppercase tracking-wider opacity-70">Closures</div>
        <select className="vb-input" style={{ height: 26, padding: "0 8px", fontSize: 12 }}
          value={year} onChange={(e) => setYear(+e.target.value)}
          data-testid="closures-year">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-[11px] opacity-60">· {closures.length} in this year</span>
        {canEdit && (
          <div className="ml-auto flex gap-1.5">
            <button className="vb-btn ghost text-xs" onClick={downloadTemplate}
              data-testid="closures-template">
              <Download size={12} /> Template
            </button>
            <button className="vb-btn ghost text-xs" onClick={() => fileRef.current?.click()}
              data-testid="closures-import">
              <Upload size={12} /> Import CSV
            </button>
            <input type="file" ref={fileRef} accept=".csv" style={{ display: "none" }}
              onChange={(e) => uploadCsv(e.target.files?.[0])} />
            <button className="vb-btn text-xs" onClick={() => setAdding(true)}
              data-testid="closures-add">
              <Plus size={12} /> Add
            </button>
          </div>
        )}
      </div>

      {adding && (
        <div className="mb-2 p-2 rounded flex flex-wrap items-end gap-2"
          style={{ background: "var(--vb-bg-2, #faf6f1)", border: "1px solid var(--vb-border)" }}
          data-testid="closure-add-form">
          <div>
            <label className="text-[10px] opacity-70 block mb-0.5">Date</label>
            <input type="date" className="vb-input" value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              style={{ height: 30, fontSize: 12 }} data-testid="closure-add-date" />
          </div>
          <div>
            <label className="text-[10px] opacity-70 block mb-0.5">Type</label>
            <select className="vb-input" value={newType}
              onChange={(e) => setNewType(e.target.value)}
              style={{ height: 30, fontSize: 12 }} data-testid="closure-add-type">
              <option value="PH">PH · Public Holiday</option>
              <option value="CL">CL · Closed</option>
            </select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="text-[10px] opacity-70 block mb-0.5">Reason (optional)</label>
            <input className="vb-input" value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              placeholder="e.g. New Year Day" style={{ height: 30, fontSize: 12 }}
              data-testid="closure-add-reason" />
          </div>
          <button className="vb-btn text-xs" onClick={addClosure} disabled={busy}
            data-testid="closure-add-save">Save</button>
          <button className="vb-btn ghost text-xs" onClick={() => setAdding(false)}>Cancel</button>
        </div>
      )}

      {closures.length === 0 ? (
        <div className="text-[12px] opacity-60 py-2" data-testid="closures-empty">
          No closures logged for {year}. Import your public holidays or add ad-hoc closures above.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
          {closures.map((c) => (
            <div key={c.id}
              className="flex items-center gap-2 p-1.5 rounded"
              style={{
                background: c.type === "PH" ? "rgba(37,99,235,0.06)" : "rgba(220,38,38,0.06)",
                border: `1px solid ${c.type === "PH" ? "rgba(37,99,235,0.25)" : "rgba(220,38,38,0.25)"}`,
              }}
              data-testid={`closure-row-${c.id}`}>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                color: "white",
                background: c.type === "PH" ? "#2563eb" : "#b91c1c",
              }}>{c.type}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium">{c.date}</div>
                {c.reason && <div className="text-[10px] opacity-60 truncate">{c.reason}</div>}
              </div>
              {canEdit && (
                <button onClick={() => removeClosure(c.id)}
                  className="opacity-50 hover:opacity-100"
                  data-testid={`closure-remove-${c.id}`}
                  title="Remove">
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="mt-3 pt-2 flex items-center gap-3 text-[10px] opacity-70"
        style={{ borderTop: "1px solid var(--vb-border)" }}>
        <span><span style={{ background: "#2563eb", color: "white", padding: "1px 5px", borderRadius: 3, fontWeight: 700 }}>PH</span> Public Holiday</span>
        <span><span style={{ background: "#b91c1c", color: "white", padding: "1px 5px", borderRadius: 3, fontWeight: 700 }}>CL</span> Closed</span>
        <span className="ml-auto flex items-center gap-1">
          <AlertTriangle size={10} /> Downstream reports will label these cells with PH / CL and skip completeness checks.
        </span>
      </div>
    </div>
  );
}
